import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import SectionTitle from '@/components/base/SectionTitle';
import HeroCarousel from '@/pages/home/components/HeroCarousel';
import { usePageMeta } from '@/hooks/usePageMeta';
import {
  getLatestMovies,
  getHotMoviesFallback,
  getLatestNews,
  getAllGenres,
  formatViews,
  formatDate,
} from '@/utils/media';
import { movies } from '@/mocks/movies';
import { news } from '@/mocks/news';

const CATEGORY_ENTRIES = [
  {
    type: 'movie' as const,
    title: '电影专区',
    desc: '院线大片、口碑佳作、高分科幻一次看够',
    icon: 'ri-film-line',
    image:
      'https://readdy.ai/api/search-image?query=Cinematic%20movie%20theater%20concept%2C%20row%20of%20seats%20facing%20a%20giant%20glowing%20green%20screen%2C%20popcorn%20and%20projector%20light%20beam%2C%20warm%20cinematic%20atmosphere&width=800&height=520&seq=nc-cat-entry-movie&orientation=landscape',
    count: 14,
  },
  {
    type: 'tv' as const,
    title: '电视剧专区',
    desc: '热播剧集、悬疑探案、古装权谋持续更新',
    icon: 'ri-tv-2-line',
    image:
      'https://readdy.ai/api/search-image?query=Cozy%20living%20room%20at%20night%2C%20television%20screen%20glowing%20green%2C%20warm%20lamp%20and%20sofa%2C%20cinematic%20lifestyle%20atmosphere%2C%20editorial%20photography&width=800&height=520&seq=nc-cat-entry-tv&orientation=landscape',
    count: 10,
  },
];

export default function Home() {
  usePageMeta(
    '嫩草影院 - 最新电影电视剧在线播放与资讯',
    '嫩草影院提供丰富的最新电影、电视剧资源和相关新闻资讯，涵盖多类型影视内容，满足用户在线观看和了解影视动态的需求。',
    '嫩草影院,电影,电视剧,影视资源,在线观影,影视资讯,最新电影,剧集更新',
  );

  const latestMovies = getLatestMovies('movie', 8);
  const latestTv = getLatestMovies('tv', 8);
  const hotList = getHotMoviesFallback(6);
  const latestNews = getLatestNews(5);

  return (
    <div className="flex min-h-screen flex-col bg-background-950">
      <Navbar />

      <main className="flex-1">
        <HeroCarousel />

        {/* 分类快捷入口 */}
        <section className="mx-auto w-full px-4 py-10 md:px-6 md:py-12">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {CATEGORY_ENTRIES.map((entry) => (
              <Link
                key={entry.type}
                to={`/category?type=${entry.type}`}
                className="group relative flex h-40 items-center overflow-hidden rounded-lg border border-background-800 bg-background-900 md:h-44"
              >
                <img
                  src={entry.image}
                  alt={entry.title}
                  title={`${entry.title} - 嫩草影院`}
                  className="absolute inset-0 h-full w-full object-top opacity-45 transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-background-950 via-background-950/70 to-transparent" />
                <div className="relative z-10 px-6">
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-500 text-background-950">
                    <i className={`${entry.icon} text-2xl`} />
                  </span>
                  <h3 className="mt-3 font-heading text-xl font-bold text-foreground-50">
                    {entry.title}
                  </h3>
                  <p className="mt-1.5 max-w-xs text-sm text-foreground-300">{entry.desc}</p>
                  <span className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary-400">
                    共 {entry.count} 部资源
                    <i className="ri-arrow-right-line" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* 主内容两栏 */}
        <section className="mx-auto w-full px-4 pb-12 md:px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <SectionTitle
                title="最新电影"
                subtitle="按更新日期倒序，第一时间发现刚上线的好片"
                icon="ri-film-line"
                moreTo="/category?type=movie"
              />
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
                {latestMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>

              <div className="mt-12">
                <SectionTitle
                  title="最新电视剧"
                  subtitle="持续更新的热播剧集，追剧不掉队"
                  icon="ri-tv-2-line"
                  moreTo="/category?type=tv"
                />
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
                  {latestTv.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
              </div>
            </div>

            {/* 侧栏 */}
            <aside className="flex flex-col gap-10">
              <div className="rounded-lg border border-background-800 bg-background-900 p-5">
                <SectionTitle
                  title="热门推荐"
                  subtitle="按播放量排序"
                  icon="ri-fire-line"
                  moreTo="/category?type=movie"
                  moreLabel="更多"
                />
                <ul className="flex flex-col gap-3">
                  {hotList.map((movie, i) => (
                    <li key={movie.id}>
                      <Link
                        to={`/detail/${movie.id}`}
                        className="group flex items-center gap-3 rounded-md p-1.5 transition-colors hover:bg-background-800"
                      >
                        <span
                          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-sm font-bold ${
                            i < 3
                              ? 'bg-accent-500 text-background-950'
                              : 'bg-background-800 text-foreground-400'
                          }`}
                        >
                          {i + 1}
                        </span>
                        <div className="h-[62px] w-[44px] shrink-0 overflow-hidden rounded bg-background-800">
                          <img
                            src={movie.cover}
                            alt={movie.title}
                            title={`${movie.title} - 嫩草影院热门`}
                            className="h-full w-full object-top"
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="truncate text-sm font-medium text-foreground-100 transition-colors group-hover:text-primary-400">
                            {movie.title}
                          </h4>
                          <p className="mt-1 truncate text-xs text-foreground-500">
                            {movie.year} · {movie.genre.slice(0, 2).join(' / ')}
                          </p>
                          <p className="mt-1 flex items-center gap-1 text-xs text-accent-500">
                            <i className="ri-fire-line" />
                            {formatViews(movie.views)}
                          </p>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-lg border border-background-800 bg-background-900 p-5">
                <SectionTitle
                  title="最新资讯"
                  subtitle="影视动态实时掌握"
                  icon="ri-newspaper-line"
                  moreTo="/news"
                  moreLabel="更多"
                />
                <ul className="flex flex-col">
                  {latestNews.map((item) => (
                    <li key={item.id}>
                      <Link
                        to={`/news/${item.id}`}
                        className="group flex gap-3 border-b border-background-800 py-3 last:border-b-0"
                      >
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500" />
                        <div className="min-w-0">
                          <h4 className="line-clamp-2 text-sm leading-snug text-foreground-200 transition-colors group-hover:text-primary-400">
                            {item.title}
                          </h4>
                          <p className="mt-1 text-xs text-foreground-500">
                            {formatDate(item.publishTime)}
                          </p>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </section>

        {/* 网站简介 */}
        <section className="border-t border-background-800 bg-background-900/60">
          <div className="mx-auto grid w-full grid-cols-1 gap-8 px-4 py-12 md:grid-cols-3 md:px-6 md:py-14">
            <div className="md:col-span-2">
              <h2 className="font-heading text-xl font-bold text-foreground-50 md:text-2xl">
                关于嫩草影院
              </h2>
              <p className="mt-4 text-sm leading-7 text-foreground-300">
                嫩草影院是一个聚焦“最新电影、电视剧在线播放与资讯”的综合影视门户。我们汇聚科幻、悬疑、都市、古装、
                爱情、家庭等多种类型的影视资源，并提供清晰分类、精准筛选与详情查看，帮助你快速找到想看的内容。
              </p>
              <p className="mt-3 text-sm leading-7 text-foreground-400">
                除了影视资源，嫩草影院还持续更新影视行业资讯、票房动态与剧集热点，让你在追剧的同时不错过任何一条重要动态。
                本站内容均为演示示例，如需了解更多，欢迎前往关于我们页面。
              </p>
              <Link
                to="/about"
                className="mt-6 inline-flex h-11 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-semibold text-background-950 transition-colors hover:bg-primary-400"
              >
                了解品牌故事
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="grid grid-cols-3 gap-4 md:grid-cols-1 md:gap-5">
              {[
                { num: String(movies.length), label: '精选影视', icon: 'ri-film-line' },
                { num: String(news.length), label: '资讯内容', icon: 'ri-newspaper-line' },
                { num: String(getAllGenres().length), label: '影视类型', icon: 'ri-price-tag-3-line' },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-lg border border-background-800 bg-background-900 p-4"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-500/15 text-accent-500">
                    <i className={`${stat.icon} text-lg`} />
                  </span>
                  <p className="mt-3 font-heading text-2xl font-extrabold text-foreground-50">
                    {stat.num}
                  </p>
                  <p className="mt-1 text-xs text-foreground-400">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}